import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import type { EChartsOption, EChartsType } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { getDayOfWeek } from 'src/app/utils/date';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { datePartDateTrunc } from 'src/app/utils/sql';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { XYChartDashoardItemFactoryOptions } from '../common/items/xy-chart';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { RealtimeStatsMainDataLoaderFactory } from './data-loaders/realtime-stats-main';
import { RealtimeStatsPurchaseCompleteDataLoaderFactory } from './data-loaders/realtime-stats-purchase-complete';
import { RealtimeStatsPurchaseFailedDataLoaderFactory } from './data-loaders/realtime-stats-purchase-failed';
import { MopaRealtimeStatsChartDocs } from './mopa-realtime-stats.chart-documentation';
import { BiVizRealtimeStatsDatasource } from './sql-maps/biviz-realtime-stats-sql-map';

@Component({
    selector: 'app-mopa-realtime-stats',
    templateUrl: './mopa-realtime-stats.component.html',
    styleUrls: ['./mopa-realtime-stats.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaRealtimeStatsComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Realtime Stats';
    override DASHBOARD_ID = 'mopa-realtime-stats';

    values = Object.values;

    private defaultGcpProjectId: GcpProjectId =
        currentDataProject().gcpProjectId;

    isDevProject = currentDataProject().key === 'mopadev';

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.realtime_stats.filter.activity_hour': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.realtime_stats.filter.activity_hour',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_HOUR',
            datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
            // orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_HOUR'),
        }).create(),
        'mopa.realtime_stats.filter.activity_weekday': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.realtime_stats.filter.activity_weekday',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_WEEKDAY',
            datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_WEEKDAY'),
        }).create(),
        'mopa.realtime_stats.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.realtime_stats.filter.app_version',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'mopa.realtime_stats.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.realtime_stats.filter.firebase_app_store',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_APP_STORE',
                datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
            })
        ).create(),
        'mopa.realtime_stats.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.realtime_stats.filter.firebase_country_tier',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
            // filterContext: ['FIREBASE_COUNTRY'],
        }).create(),
        'mopa.realtime_stats.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.realtime_stats.filter.firebase_country',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.realtime_stats.filter.firebase_country_us_region':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_stats.filter.firebase_country_us_region',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_US_REGION',
                datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
            }).create(),
        'mopa.realtime_stats.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_stats.filter.firebase_country_european_union',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
            }).create(),
        'mopa.realtime_stats.filter.event_project_id': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.realtime_stats.filter.event_project_id',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'EVENT_PROJECT_ID',
            datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
        }).create(),
        'mopa.realtime_stats.filter.sales_inapp_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.realtime_stats.filter.sales_inapp_source',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_INAPP_SOURCE',
            datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
        }).create(),
        'mopa.realtime_stats.filter.realtime_purchase_error':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.realtime_stats.filter.realtime_purchase_error',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'REALTIME_PURCHASE_ERROR',
                datasourceSqlMap: BiVizRealtimeStatsDatasource.sqlMap,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.realtime_stats.modify_time': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId:
                'mopa.realtime_stats.modify_time_realtime_stats_datasource',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: BiVizRealtimeStatsDatasource.sqlMap.dataset,
            tableId: BiVizRealtimeStatsDatasource.sqlMap.tableId,
        }).create(),

        'mopa.realtime_stats.realtime_stats_main':
            new RealtimeStatsMainDataLoaderFactory({
                createdInstanceId: 'mopa.realtime_stats.realtime_stats_main',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'mopa.realtime_stats.realtime_stats_purchase_complete':
            new RealtimeStatsPurchaseCompleteDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_stats.realtime_stats_purchase_complete',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
        'mopa.realtime_stats.realtime_stats_purchase_failed':
            new RealtimeStatsPurchaseFailedDataLoaderFactory({
                createdInstanceId:
                    'mopa.realtime_stats.realtime_stats_purchase_failed',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    settingItemDatetimeAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATETIME_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'hour' as datePartDateTrunc,
            selectOptions: [
                'hour',
                'day',
                'week',
                'month',
                'quarter',
                'year',
            ] as 'hour' | datePartDateTrunc[],
        },
    };

    settingsItemRealtimeNormalizeMetrics: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.REALTIME_NORMALIZE_METRICS,
            viewtype: 'toggle',
            displayName: 'Normalize metrics',
            value: 'none',
            selectOptions: ['none', 'dau'],
        },
    };

    settingRealtimeHoursUpToLatest: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.REALTIME_HOURS_UP_TO_LATEST,
            viewtype: 'checkbox',
            displayName: 'Filter up to latest hour',
            value: this.getInitialContext().settings.realtimeHoursUpToLatest,
        },
    };

    settingRealtimeCurrentWeekday: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.REALTIME_CURRENT_WEEKDAY,
            viewtype: 'checkbox',
            displayName: 'Filter current weekday',
            value: false,
        },
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'mopa.realtime_stats.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.realtime_stats.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
                rangePresets: [
                    'lifetime',
                    'today',
                    'yesterday',
                    'last 7 days',
                    'last 14 days',
                    'last 30 days',
                    'last 60 days',
                    'last 90 days',
                    'last 180 days',
                    'last 365 days',
                    'custom',
                ],
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_WEEKDAY: {
            id: 'mopa.realtime_stats.filter.activity_weekday',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.activity_weekday',
                    'ACTIVITY_WEEKDAY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.activity_weekday'
                ].emitter,
        },
        ACTIVITY_HOUR: {
            id: 'mopa.realtime_stats.filter.activity_hour',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.activity_hour',
                    'ACTIVITY_HOUR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.activity_hour'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.realtime_stats.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.activity_app_version'
                ].emitter,
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.realtime_stats.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.realtime_stats.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.realtime_stats.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.realtime_stats.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.firebase_country'
                ].emitter,
        },
        FIREBASE_COUNTRY_US_REGION: {
            id: 'mopa.realtime_stats.filter.firebase_country_us_region',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.firebase_country_us_region',
                    'FIREBASE_COUNTRY_US_REGION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.firebase_country_us_region'
                ].emitter,
        },
        SALES_INAPP_SOURCE: {
            id: 'mopa.realtime_stats.filter.sales_inapp_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.sales_inapp_source',
                    'SALES_INAPP_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.sales_inapp_source'
                ].emitter,
        },
        REALTIME_PURCHASE_ERROR: {
            id: 'mopa.realtime_stats.filter.realtime_purchase_error',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.realtime_purchase_error',
                    'REALTIME_PURCHASE_ERROR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.realtime_purchase_error'
                ].emitter,
        },
        EVENT_PROJECT_ID: {
            id: 'mopa.realtime_stats.filter.event_project_id',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.realtime_stats.filter.event_project_id',
                    'EVENT_PROJECT_ID'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.realtime_stats.filter.event_project_id'
                ].emitter,
        },
    };

    // CHARTS HELPERS

    chartOptionsDashboardItemHelper(
        options: Pick<
            XYChartDashoardItemFactoryOptions,
            | 'createdInstanceId'
            | 'dashboardItem'
            | 'yColumnLabel'
            | 'metricName'
            | 'yAxisLabel'
            | 'tooltip'
        > & {
            /** Formatter used when normalizing metrics is enabled */
            normalizedMetricTooltipFormatter?: (value: number) => string;
            isNormalizable?: boolean;
        }
    ): XYChartDashoardItemFactoryOptions {
        // cannot define options.tooltip and options.normalizedMetricTooltipFormatter at the same time
        if (options.tooltip && options.normalizedMetricTooltipFormatter) {
            throw new Error(
                'Cannot define both options.tooltip and options.normalizedMetricTooltipFormatter'
            );
        }
        return {
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: options.createdInstanceId,
            xAxisType: 'category',
            xAxisCategorySubtype: 'datetime',
            xAxisCategoryDatetimeFilterGetter:
                MopaRealtimeStatsComponent.xAxisCategoryDatetimeCurrentWeekdayFilterGetter,
            xColumnLabel: 'datetime',
            yColumnLabel: options.yColumnLabel,
            metricName: options.metricName,
            echartsSeriesType: 'bar',
            dashboardItem: {
                ...options.dashboardItem,
                subtitle:
                    options.dashboardItem.subtitle ??
                    ((context) => {
                        const normalizationType =
                            context.settings['realtimeNormaliseMetrics'] ??
                            QueryContextDefaultSettings[
                                QueryContextSettingID.REALTIME_NORMALIZE_METRICS
                            ]!;

                        return normalizationType === 'none'
                            ? ''
                            : `Normalized by: ${normalizationType}`;
                    }),
            },

            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from splitColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            tooltip: options.tooltip ?? {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    // if normalizing metrics, use tooltipPercentValueFormatter, else use tooltipIntegerValueFormatter
                    return context.settings['realtimeNormaliseMetrics'] !=
                        'none'
                        ? (options.normalizedMetricTooltipFormatter ??
                              EChartsTooltipHelper.tooltipPercentValueFormatter)
                        : EChartsTooltipHelper.tooltipIntegerValueFormatter;
                },
            },

            echartsOptionsOverride: (
                context: IQueryContext,
                currentOptions: EChartsOption,
                _echartsInstance: EChartsType
            ) => {
                if (options.isNormalizable === false) {
                    currentOptions.yAxis = {
                        axisLabel: {
                            formatter:
                                EChartsAxisHelper.getAxisPercentLabelFormatter(
                                    0
                                ),
                        },
                    };
                } else if (
                    context.settings['realtimeNormaliseMetrics'] !== 'none'
                ) {
                    currentOptions.yAxis = options.yAxisLabel ?? {
                        axisLabel: {
                            formatter:
                                EChartsAxisHelper.getAxisPercentLabelFormatter(
                                    2
                                ),
                        },
                    };
                } else {
                    currentOptions.yAxis = options.yAxisLabel ?? {
                        axisLabel: {
                            formatter:
                                EChartsAxisHelper.axisNumberLabelFormatter,
                        },
                    };
                }
            },
        };
    }

    // CHARTS
    modify_time_users_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.modify_time_users_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.realtime_stats.modify_time_users_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.modify_time'].emitter,
    };

    purchase_complete_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.purchase_complete_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.purchase_complete_cnt',
                dashboardItem: {
                    title: 'Purchase complete count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.purchase_complete_cnt,
                },
                yColumnLabel: 'PurchaseComplete_count',
                metricName: 'Purchase complete count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_complete'
            ].emitter,
    };

    purchase_complete_users_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.purchase_complete_users_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.purchase_complete_users_cnt',
                dashboardItem: {
                    title: 'Purchase complete users count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.purchase_complete_users_cnt,
                },
                yColumnLabel: 'PurchaseComplete_users_count',
                metricName: 'Purchase complete users count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_complete'
            ].emitter,
    };

    product_consumed_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.product_consumed_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.product_consumed_cnt',
                dashboardItem: {
                    title: 'Product consumed count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.product_consumed_cnt,
                },
                yColumnLabel: 'ProductConsumed_count',
                metricName: 'Product consumed count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_complete'
            ].emitter,
    };

    consume_failed_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.consume_failed_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.consume_failed_cnt',
                dashboardItem: {
                    title: 'Consume failed count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.consume_failed_cnt,
                },
                yColumnLabel: 'ConsumeFailed_count',
                metricName: 'Consume failed count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_complete'
            ].emitter,
    };

    unconsumed_purchases_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.unconsumed_purchases_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.unconsumed_purchases_cnt',
                dashboardItem: {
                    title: 'Unconsumed purchase count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.unconsumed_purchases_cnt,
                },
                yColumnLabel: 'UnconsumedPurchases_count',
                metricName: 'Unconsumed purchases count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_complete'
            ].emitter,
    };

    unconsumed_purchases_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.unconsumed_purchases_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.unconsumed_purchases_rate',
                dashboardItem: {
                    title: 'Unconsumed purchase rate',
                    subtitle: '',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.unconsumed_purchases_rate,
                },
                isNormalizable: false,
                yColumnLabel: 'UnconsumedPurchases_rate',
                metricName: 'Unconsumed purchases rate',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_complete'
            ].emitter,
    };

    purchase_failed_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.purchase_failed_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.purchase_failed_cnt',
                dashboardItem: {
                    title: 'Purchase failed count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.purchase_failed_cnt,
                },
                yColumnLabel: 'PurchaseFailed_count',
                metricName: 'Purchase failed count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_failed'
            ].emitter,
    };

    purchase_failed_users_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.purchase_failed_users_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.purchase_failed_users_cnt',
                dashboardItem: {
                    title: 'Purchase failed users count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.purchase_failed_users_cnt,
                },
                yColumnLabel: 'PurchaseFailed_users_count',
                metricName: 'Purchase failed users count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_failed'
            ].emitter,
    };

    purchase_failed_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.purchase_failed_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.purchase_failed_rate',
                dashboardItem: {
                    title: 'Purchase failed rate',
                    // Subtitle is empty because it's a chart that is not affected by metric normalization
                    subtitle: '',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.purchase_failed_rate,
                },
                isNormalizable: false,
                yColumnLabel: 'PurchaseFailed_rate',
                metricName: 'Purchase failed rate',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_failed'
            ].emitter,
    };

    purchase_failed_users_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.purchase_failed_users_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.purchase_failed_users_rate',
                dashboardItem: {
                    title: 'Purchase failed users rate',
                    // Subtitle is empty because it's a chart that is not affected by metric normalization
                    subtitle: '',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.purchase_failed_users_rate,
                },
                isNormalizable: false,
                yColumnLabel: 'PurchaseFailed_users_rate',
                metricName: 'Purchase failed users rate',
                yAxisLabel: {
                    formatter:
                        EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) =>
                            EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                                label,
                                context
                            );
                    },
                    valueFormatterGetter(context) {
                        return EChartsTooltipHelper.tooltipPercentValueFormatter;
                    },
                },
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.realtime_stats.realtime_stats_purchase_failed'
            ].emitter,
    };

    ad_reward_granted_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.ad_reward_granted_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.ad_reward_granted_cnt',
                dashboardItem: {
                    title: 'Ad reward granted count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.ad_reward_granted_cnt,
                },
                yColumnLabel: 'AdRewardGranted_count',
                metricName: 'Ad reward granted count',
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId:
            //         'mopa.realtime_stats.chart.ad_reward_granted_cnt',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Ad reward granted count' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'AdRewardGranted_count',
            //     metricName: 'Ad reward granted count',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    enter_game_content_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.enter_game_content_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.enter_game_content_cnt',
                dashboardItem: {
                    title: 'Enter game content count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.enter_game_content_cnt,
                },
                yColumnLabel: 'EnterGameContent_count',
                metricName: 'Enter game content count',
            })
            // {
            // dashboardName: this.DASHBOARD_TITLE,
            // createdInstanceId:
            //     'mopa.realtime_stats.chart.enter_game_content_cnt',
            // componentClass: 'EchartsDashboardItemComponent',
            // FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            // dashboardItem: { title: 'Enter game content count' },
            // xAxisType: 'category',
            // xColumnLabel: 'date',
            // yColumnLabel: 'EnterGameContent_count',
            // metricName: 'Enter game content count',
            // echartsSeriesType: 'bar',
            // colorGetterBuilder: (context: IQueryContext) => {
            //     // get all split columns ids
            //     const splitColumnsIds = context.datasourceColumns
            //         .filter((dc) => dc.state.isSplitApplied)
            //         .map((dc) => dc.id);
            //     // create layer from spliColumns
            //     const layer = ChartColorService.getLayerString(splitColumnsIds);
            //     return (s: string) => ({
            //         color: this.colorService.getColor(layer, s),
            //     });
            // },
            // getEchartsOptionsOverride: (context: IQueryContext) => {
            //     return {
            //         legend: { show: false },
            //         tooltip: {
            //             trigger: 'axis',
            //             transitionDuration: 0,
            //             hideDelay: 0,
            //             appendToBody: true,
            //             formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                 (label: any) =>
            //                     axisLabelDatetimeFormatter(label, context),
            //                 tooltipIntegerValueFormatter
            //             ),
            //         },
            //     };
            // },
            // echartsOptionsOverride: (
            //     context: IQueryContext,
            //     echartsOption: EChartsOption
            // ) => {
            //     echartsOption.yAxis = {
            //         axisLabel: { formatter: axisNumberLabelFormatter },
            //     };
            // },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    item_collected_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.item_collected_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.item_collected_cnt',
                dashboardItem: {
                    title: 'Item collected count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.item_collected_cnt,
                },
                yColumnLabel: 'ItemCollected_count',
                metricName: 'Item collected count',
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId: 'mopa.realtime_stats.chart.item_collected_cnt',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Item collected count' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'ItemCollected_count',
            //     metricName: 'Item collected count',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    content_download_complete_true_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.content_download_complete_true_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.content_download_complete_true_cnt',
                dashboardItem: {
                    title: 'Content download complete true count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.content_download_complete_true_cnt,
                },
                yColumnLabel: 'ContentDownloadCompleted_Success_count',
                metricName: 'Content download complete true count',
            })

            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId:
            //         'mopa.realtime_stats.chart.content_download_complete_true_cnt',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Content download complete true count' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'ContentDownloadCompleted_Success_count',
            //     metricName: 'Content download complete true count',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    gameprogress25_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.gameprogress25_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.gameprogress25_cnt',
                dashboardItem: {
                    title: 'Gameprogress25 count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.gameprogress25_cnt,
                },
                yColumnLabel: 'GameProgress_25_users_count',
                metricName: 'Gameprogress25 count',
                normalizedMetricTooltipFormatter: (value: number) =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter(
                        value,
                        undefined,
                        3
                    ),
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId: 'mopa.realtime_stats.chart.gameprogress25_cnt',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Gameprogress25 count' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'GameProgress_25_users_count',
            //     metricName: 'Gameprogress25 count',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    gameprogress50_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.gameprogress50_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.gameprogress50_cnt',
                dashboardItem: {
                    title: 'Gameprogress50 count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.gameprogress50_cnt,
                },
                yColumnLabel: 'GameProgress_50_users_count',
                metricName: 'Gameprogress50 count',
                normalizedMetricTooltipFormatter: (value: number) =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter(
                        value,
                        undefined,
                        3
                    ),
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId: 'mopa.realtime_stats.chart.gameprogress50_cnt',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Gameprogress50 count' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'GameProgress_50_users_count',
            //     metricName: 'Gameprogress50 count',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    gameprogress75_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.gameprogress75_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.gameprogress75_cnt',
                dashboardItem: {
                    title: 'Gameprogress75 count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.gameprogress75_cnt,
                },
                yColumnLabel: 'GameProgress_75_users_count',
                metricName: 'Gameprogress75 count',
                normalizedMetricTooltipFormatter: (value: number) =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter(
                        value,
                        undefined,
                        3
                    ),
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId: 'mopa.realtime_stats.chart.gameprogress75_cnt',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Gameprogress75 count' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'GameProgress_75_users_count',
            //     metricName: 'Gameprogress75 count',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    finish_game_content_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.finish_game_content_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.finish_game_content_cnt',
                dashboardItem: {
                    title: 'FinishGameContent count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.finish_game_content_cnt,
                },
                yColumnLabel: 'FinishGameContent_count',
                metricName: 'FinishGameContent count',
                normalizedMetricTooltipFormatter: (value: number) =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter(
                        value,
                        undefined,
                        3
                    ),
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_energy_in: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_energy_in',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_energy_in',
                dashboardItem: {
                    title: 'Resource amount changed energy in',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_energy_in,
                },
                yColumnLabel: 'ResourceAmountChanged_Energy_in',
                metricName: 'Resource amount changed energy in',
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId:
            //         'mopa.realtime_stats.chart.resource_amount_changed_energy_in',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Resource amount changed energy in' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'ResourceAmountChanged_Energy_in',
            //     metricName: 'Resource amount changed energy in',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_energy_out: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_energy_out',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_energy_out',
                dashboardItem: {
                    title: 'Resource amount changed energy out',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_energy_out,
                },
                yColumnLabel: 'ResourceAmountChanged_Energy_out',
                metricName: 'Resource amount changed energy out',
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId:
            //         'mopa.realtime_stats.chart.resource_amount_changed_energy_out',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Resource amount changed energy out' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'ResourceAmountChanged_Energy_out',
            //     metricName: 'Resource amount changed energy out',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_gems_in: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_gems_in',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_gems_in',
                dashboardItem: {
                    title: 'Resource amount changed gems in',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_gems_in,
                },
                yColumnLabel: 'ResourceAmountChanged_Gems_in',
                metricName: 'Resource amount changed gems in',
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId:
            //         'mopa.realtime_stats.chart.resource_amount_changed_gems_in',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Resource amount changed gems in' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'ResourceAmountChanged_Gems_in',
            //     metricName: 'Resource amount changed gems in',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_gems_out: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_gems_out',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_gems_out',
                dashboardItem: {
                    title: 'Resource amount changed gems out',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_gems_out,
                },
                yColumnLabel: 'ResourceAmountChanged_Gems_out',
                metricName: 'Resource amount changed gems out',
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId:
            //         'mopa.realtime_stats.chart.resource_amount_changed_gems_out',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Resource amount changed gems out' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'ResourceAmountChanged_Gems_out',
            //     metricName: 'Resource amount changed gems out',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_hints_in: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_hints_in',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_hints_in',
                dashboardItem: {
                    title: 'Resource amount changed hints in',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_hints_in,
                },
                yColumnLabel: 'ResourceAmountChanged_Hints_in',
                metricName: 'Resource amount changed hints in',
            })
            //     {
            //     dashboardName: this.DASHBOARD_TITLE,
            //     createdInstanceId:
            //         'mopa.realtime_stats.chart.resource_amount_changed_hints_in',
            //     componentClass: 'EchartsDashboardItemComponent',
            //     FACTORY_CLASS: 'DauTimeseriesXYChartDashoardItemFactoryOptions',
            //     dashboardItem: { title: 'Resource amount changed hints in' },
            //     xAxisType: 'category',
            //     xColumnLabel: 'date',
            //     yColumnLabel: 'ResourceAmountChanged_Hints_in',
            //     metricName: 'Resource amount changed hints in',
            //     echartsSeriesType: 'bar',
            //     colorGetterBuilder: (context: IQueryContext) => {
            //         // get all split columns ids
            //         const splitColumnsIds = context.datasourceColumns
            //             .filter((dc) => dc.state.isSplitApplied)
            //             .map((dc) => dc.id);
            //         // create layer from spliColumns
            //         const layer = ChartColorService.getLayerString(splitColumnsIds);
            //         return (s: string) => ({
            //             color: this.colorService.getColor(layer, s),
            //         });
            //     },
            //     getEchartsOptionsOverride: (context: IQueryContext) => {
            //         return {
            //             legend: { show: false },
            //             tooltip: {
            //                 trigger: 'axis',
            //                 transitionDuration: 0,
            //                 hideDelay: 0,
            //                 appendToBody: true,
            //                 formatter: EChartsTooltipHelper.createTooltipFormatter(
            //                     (label: any) =>
            //                         axisLabelDatetimeFormatter(label, context),
            //                     tooltipIntegerValueFormatter
            //                 ),
            //             },
            //         };
            //     },
            //     echartsOptionsOverride: (
            //         context: IQueryContext,
            //         echartsOption: EChartsOption
            //     ) => {
            //         echartsOption.yAxis = {
            //             axisLabel: { formatter: axisNumberLabelFormatter },
            //         };
            //     },
            // }
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_hints_out: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_hints_out',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_hints_out',
                dashboardItem: {
                    title: 'Resource amount changed hints out',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_hints_out,
                },
                yColumnLabel: 'ResourceAmountChanged_Hints_out',
                metricName: 'Resource amount changed hints out',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_coins_in: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_coins_in',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_coins_in',
                dashboardItem: {
                    title: 'Resource amount changed coins in',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_coins_in,
                },
                yColumnLabel: 'ResourceAmountChanged_Coins_in',
                metricName: 'Resource amount changed coins in',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_coins_out: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_coins_out',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_coins_out',
                dashboardItem: {
                    title: 'Resource amount changed coins out',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_coins_out,
                },
                yColumnLabel: 'ResourceAmountChanged_Coins_out',
                metricName: 'Resource amount changed coins out',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_compasses_in: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_compasses_in',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_compasses_in',
                dashboardItem: {
                    title: 'Resource amount changed compasses in',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_compasses_in,
                },
                yColumnLabel: 'ResourceAmountChanged_Compass_in',
                metricName: 'Resource amount changed compasses in',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    resource_amount_changed_compasses_out: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.resource_amount_changed_compasses_out',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.resource_amount_changed_compasses_out',
                dashboardItem: {
                    title: 'Resource amount changed compasses out',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.resource_amount_changed_compasses_out,
                },
                yColumnLabel: 'ResourceAmountChanged_Compass_out',
                metricName: 'Resource amount changed compasses out',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    decoration_amount_changed_owned_amount_sum: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.decoration_amount_changed_owned_amount_sum',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.decoration_amount_changed_owned_amount_sum',
                dashboardItem: {
                    title: 'Decoration Owned Amount sum',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.decoration_amount_changed_owned_amount_sum,
                },
                yColumnLabel: 'DecorationAmountChanged_OwnedAmount_sum',
                metricName: 'Decoration Owned Amount sum',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    decoration_amount_changed_map_amount_sum: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.decoration_amount_changed_map_amount_sum',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.decoration_amount_changed_map_amount_sum',
                dashboardItem: {
                    title: 'New Decos Placed sum',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.decoration_amount_changed_map_amount_sum,
                },
                yColumnLabel: 'DecorationAmountChanged_MapAmount_sum',
                metricName: 'New Decos Placed sum',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    unlock_segment_flow_closed_success_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.unlock_segment_flow_closed_success_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.unlock_segment_flow_closed_success_cnt',
                dashboardItem: {
                    title: 'Unlocked Segments count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.unlock_segment_flow_closed_success_cnt,
                },
                yColumnLabel: 'UnlockSegmentFlowClosed_Success_count',
                metricName: 'Unlocked Segments count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // 'CloudSaveCommitStarted_count',
    cloud_save_commit_started_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_commit_started_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_commit_started_cnt',
                dashboardItem: {
                    title: 'CS Commit Started count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_commit_started_cnt,
                },
                yColumnLabel: 'CloudSaveCommitStarted_count',
                metricName: 'CS Commit Started count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // 'CloudSaveCommitCompleted_Success_count',
    cloud_save_commit_completed_success_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_commit_completed_success_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_commit_completed_success_cnt',
                dashboardItem: {
                    title: 'CS Commit Completed Success count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_commit_completed_success_cnt,
                },
                yColumnLabel: 'CloudSaveCommitCompleted_Success_count',
                metricName: 'CS Commit Completed Success count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // 'CloudSaveConflictDetected_count',
    cloud_save_conflict_detected_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_conflict_detected_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_conflict_detected_cnt',
                dashboardItem: {
                    title: 'CS Conflict Detected count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_conflict_detected_cnt,
                },
                yColumnLabel: 'CloudSaveConflictDetected_count',
                metricName: 'CS Conflict Detected count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // 'CloudSaveConflictResolved_Success_count',
    cloud_save_conflict_resolved_success_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_conflict_resolved_success_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_conflict_resolved_cnt',
                dashboardItem: {
                    title: 'CS Conflict Resolved Success count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_conflict_resolved_success_cnt,
                },
                yColumnLabel: 'CloudSaveConflictResolved_Success_count',
                metricName: 'CS Conflict Resolved Success count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // 'CloudSaveDoubleSessionDetected_count',
    cloud_save_double_session_detected_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_double_session_detected_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_double_session_detected_cnt',
                dashboardItem: {
                    title: 'CS Double Session Detected count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_double_session_detected_cnt,
                },
                yColumnLabel: 'CloudSaveDoubleSessionDetected_count',
                metricName: 'CS Double Session Detected count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // 'CloudSaveLoginStarted_count',
    cloud_save_login_started_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_login_started_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_login_started_cnt',
                dashboardItem: {
                    title: 'CS Login Started count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_login_started_cnt,
                },
                yColumnLabel: 'CloudSaveLoginStarted_count',
                metricName: 'CS Login Started count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // 'CloudSaveLoginCompleted_Success_count',
    cloud_save_login_completed_success_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_login_completed_success_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_login_completed_success_cnt',
                dashboardItem: {
                    title: 'CS Login Completed Success count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_login_completed_success_cnt,
                },
                yColumnLabel: 'CloudSaveLoginCompleted_Success_count',
                metricName: 'CS Login Completed Success count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // 'CloudSaveRetryCloudOperation_count',
    cloud_save_retry_cloud_operation_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_retry_cloud_operation_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_retry_cloud_operation_cnt',
                dashboardItem: {
                    title: 'CS Retry Cloud Operation count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_retry_cloud_operation_cnt,
                },
                yColumnLabel: 'CloudSaveRetryCloudOperation_count',
                metricName: 'CS Retry Cloud Operation count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // CloudSaveIncompleteSaveDetected
    cloud_save_incomplete_save_detected_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_incomplete_save_detected_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_incomplete_save_detected_cnt',
                dashboardItem: {
                    title: 'CS Incomplete Save Detected count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_incomplete_save_detected_cnt,
                },
                yColumnLabel: 'CloudSaveIncompleteSaveDetected_count',
                metricName: 'CS Incomplete Save Detected count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // CloudSaveIncompleteSaveResolved
    cloud_save_incomplete_save_resolved_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_incomplete_save_resolved_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_incomplete_save_resolved_cnt',
                dashboardItem: {
                    title: 'CS Incomplete Save Resolved count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_incomplete_save_resolved_cnt,
                },
                yColumnLabel: 'CloudSaveIncompleteSaveResolved_count',
                metricName: 'CS Incomplete Save Resolved count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // CloudSavePurgeIncompleteStorage
    cloud_save_purge_incomplete_storage_sum: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.cloud_save_purge_incomplete_storage_sum',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.cloud_save_purge_incomplete_storage_sum',
                dashboardItem: {
                    title: 'CS Purge Incomplete Storage sum',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.cloud_save_purge_incomplete_storage_sum,
                },
                yColumnLabel: 'CloudSavePurgeIncompleteStorage_sum',
                metricName: 'CS Purge Incomplete Storage sum',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };
    // DialogOpened_IncompleteSaveDialog_count
    dialog_opened_incomplete_save_dialog_cnt: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.realtime_stats.chart.dialog_opened_incomplete_save_dialog_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart'](
            this.chartOptionsDashboardItemHelper({
                createdInstanceId:
                    'mopa.realtime_stats.chart.dialog_opened_incomplete_save_dialog_cnt',
                dashboardItem: {
                    title: 'DialogOpened Incomplete Save Dialog count',
                    chartDocumentation:
                        MopaRealtimeStatsChartDocs.dialog_opened_incomplete_save_dialog_cnt,
                },
                yColumnLabel: 'DialogOpened_IncompleteSaveDialog_count',
                metricName: 'Dialog Opened Incomplete Save Dialog count',
            })
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.realtime_stats.realtime_stats_main']
                .emitter,
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaUsersComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'MopaUsersComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'lifetime',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            // activityDateIC,
        ];

        // default date aggregation
        initialContext.settings['datetimeAggregation'] = 'day';

        initialContext.settings['realtimeHoursUpToLatest'] = true;
        // initialContext.settings['realtimeCurrentWeekday'] = true;

        // for metrics that require normalization, default to none
        initialContext.settings[
            QueryContextSettingID.REALTIME_NORMALIZE_METRICS
        ] = 'none';

        return initialContext;
    }

    public static xAxisCategoryDatetimeCurrentWeekdayFilterGetter(
        context: IQueryContext
    ) {
        let selectedWeekdays = context.datasourceColumns
            .filter((dc) => {
                return dc.id === 'ACTIVITY_WEEKDAY' && dc.state.isFilterApplied;
            })
            .flatMap((dc) => dc.state.value.options);
        if (
            !context.settings['realtimeCurrentWeekday'] &&
            selectedWeekdays.length === 0
        )
            return () => true;
        if (typeof context.settings['realtimeCurrentWeekday'] === 'boolean')
            throw new Error(
                'realtimeCurrentWeekday setting should be a string representing the weekday'
            );
        selectedWeekdays.push(context.settings['realtimeCurrentWeekday']);
        // filter undefined or null weekdays
        selectedWeekdays = selectedWeekdays.filter((wd) => wd !== undefined);
        return (d: Date) => {
            // true if today is the same weekday as d
            // get current UTC weekday

            return selectedWeekdays.includes(getDayOfWeek(d));
        };
    }
}
